import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { GameFieldsType } from "../../../../api/types";
import { useFetchGameActions } from "../../../../api/hooks";
import { classNames } from "../../../../utils";
import { GameStatusBadge } from "./GameStatusBadge";

interface GameDetailsProps {
  game: GameFieldsType | undefined | null;
  open: boolean;
  onClose: () => void;
}

export default function GameDetails({ game, open, onClose }: GameDetailsProps) {
  const { data } = useFetchGameActions(game?.record_id);
  const isHomeWinner =
    game?.home_score && game?.away_score
      ? game.home_score > game.away_score
      : false;
  const isAwayWinner =
    game?.home_score && game?.away_score
      ? game.home_score < game.away_score
      : false;

  return (
    <Dialog as="div" open={open} onClose={onClose} className="relative z-10">
      <div className="fixed inset-0" />

      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <DialogPanel
              transition
              className="pointer-events-auto w-screen max-w-md transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
            >
              <div className="flex h-full flex-col overflow-y-scroll bg-white py-6 shadow-xl">
                <div className="px-4 sm:px-6">
                  <div className="flex items-start justify-between">
                    <DialogTitle className="text-base font-semibold text-gray-900">
                      {game?.home_team_name?.[0]} vs {game?.away_team_name?.[0]}
                    </DialogTitle>
                    <div className="ml-3 flex h-7 items-center">
                      <button
                        type="button"
                        onClick={onClose}
                        className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      >
                        <span className="absolute -inset-2.5" />
                        <span className="sr-only">Fermer</span>
                        <XMarkIcon aria-hidden="true" className="w-6 h-6" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="relative mt-6 flex-1 px-4 sm:px-6">
                  {game ? (
                    <div className="relative h-full w-full">
                      <div className="h-full w-full flex justify-center">
                        <div className="h-full w-0.5 bg-gray-100" />
                      </div>
                      <div className="absolute top-0 left-0 w-full h-full">
                        <div className="flex flex-row items-center justify-between">
                          <div className="flex flex-row items-center space-x-2">
                            {game.home_team_logo?.[0]?.url ? (
                              <img
                                className="w-8 h-8 md:w-10 md:h-10 lg:w-15 lg:h-15 rounded object-contain pr-1"
                                src={game.home_team_logo?.[0]?.url}
                                alt=""
                              />
                            ) : (
                              <div className="w-8 h-8 md:w-10 md:h-10 lg:w-15 lg:h-15 rounded-full object-contain pr-1 bg-gray-100" />
                            )}
                            {game.status !== "upcoming" ? (
                              <div className="flex flex-col items-center">
                                <p
                                  className={classNames(
                                    "text-3xl font-bold w-8",
                                    isHomeWinner
                                      ? "text-indigo-500"
                                      : "text-gray-500"
                                  )}
                                >
                                  {game.home_score}
                                </p>
                              </div>
                            ) : null}
                          </div>
                          <div className="flex flex-col items-center">
                            <GameStatusBadge
                              status={game.status}
                              kickOffDate={game.date}
                              startDate={game.start_date}
                              htStartDate={game.ht_start_date}
                            />
                          </div>
                          <div className="flex flex-row items-center space-x-2">
                            {game.status !== "upcoming" ? (
                              <div className="flex flex-col items-center">
                                <p
                                  className={classNames(
                                    "text-3xl font-bold w-8",
                                    isAwayWinner
                                      ? "text-indigo-500"
                                      : "text-gray-500"
                                  )}
                                >
                                  {game.away_score}
                                </p>
                              </div>
                            ) : null}
                            {game.away_team_logo?.[0]?.url ? (
                              <img
                                className="w-8 h-8 md:w-10 md:h-10 lg:w-15 lg:h-15 rounded object-contain"
                                src={game.away_team_logo?.[0]?.url}
                                alt=""
                              />
                            ) : (
                              <div className="w-8 h-8 md:w-10 md:h-10 lg:w-15 lg:h-15 rounded-full object-contain bg-gray-100" />
                            )}
                          </div>
                        </div>
                        <div className="space-y-4 pb-4 pt-8">
                          {data?.map((action) => (
                            <div
                              key={action.id}
                              className={classNames(
                                "flex items-center",
                                !action.fields.is_home_team
                                  ? "flex-row-reverse -mr-2"
                                  : "flex-row mr-2"
                              )}
                            >
                              <div
                                className={classNames(
                                  "flex flex-col flex-1 text-sm text-gray-500",
                                  action.fields.is_home_team
                                    ? "items-end pr-2 text-right"
                                    : "items-start pl-2 text-left"
                                )}
                              >
                                <p className="font-semibold">
                                  {action.fields.type}
                                </p>
                                <p>{`${action.fields.player_number}. ${action.fields.player_lastname}`}</p>
                              </div>
                              <div
                                className={classNames(
                                  "bg-gray-200 rounded-full px-2 pb-1"
                                )}
                              >
                                <span
                                  className={classNames(
                                    "font-semibold text-xs text-gray-500"
                                  )}
                                >
                                  {action.fields.time}
                                </span>
                              </div>
                              <div className="flex flex-1" />
                            </div>
                          ))}
                          {game.status !== "upcoming" ? (
                            <p className="text-sm font-extrabold text-gray-500 text-center py-2 bg-white">
                              Début du match
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
